import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  Menu,
  MenuItem,
  Pagination,
  PaginationItem,
} from "@mui/material";
import { baseFetch } from "../../helpers/apiClient";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { toggleSessionModal } from "../../redux/sessionSlice";
import { useLocation, useNavigate } from "react-router-dom";
import Loader from "../../components/Loader";
import CheckIcon from "@mui/icons-material/CheckCircle";
import DeleteEventPopup from "../../components/DeleteEventPopup";
import Background from "../../components/Background";
import PlusIcon from "../../assets/images/Vector.svg";
import { categoryList } from "../../constants/categories";
import { formatEventType } from "../../constants/RegionalLocationList";
import { NextSlot, PreviousSlot } from "./IndustryHub";
import useDebounce from "../../hooks/useDebounce";
import useDashboardList from "../../hooks/useDashboardList";
import PostingLimitModal from "../../components/PostingLimitModal";
import { fetchProfileInfo } from "../../redux/profileSlice";

const itemNature = ["Pending", "Upcoming", "Past", "Rejected", "Posted"];
function showDateAndTime(event) {
  if (event.eventType === "webinar" || event.eventType === "virtual-event") {
    return true;
  } else {
    return false;
  }
}
export default function ManageVirtualEvents({ searchTerm }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem("token");
  const filter = location.state;
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedItemNature, setSelectedItemNature] = useState(
    filter ? filter : "Pending",
  );
  const [open, setOpen] = useState(false);
  const [openPostLimitModal, setOpenPostLimitModal] = useState(false);
  const [deleteEventId, setDeleteEventId] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [page, setPage] = useState(1);
  const debouncedSearchCategoryText = useDebounce(searchTerm, 500);
  const contentSizeInfo = useSelector(
    (state) => state?.profileSlice?.profile?.contentSizeInfo,
  );
  const paginationSlots = {
    previous: PreviousSlot,
    next: NextSlot,
  };

  const request = useMemo(
    () => ({
      url: `/v1/web/event/list/${selectedItemNature}?page=${page}&limit=12`,
      method: "POST",
      body: {
        eventType: selectedCategory,
        eventTitle: debouncedSearchCategoryText,
      },
    }),
    [page, selectedItemNature, debouncedSearchCategoryText, selectedCategory],
  );
  const {
    data,
    loading: listLoading,
    refresh,
  } = useDashboardList(request, token);
  const eventData = data.results;
  const totalRecords = data.total;
  const loading = deleting || listLoading;

  const filterList =
    selectedItemNature === "Posted"
      ? []
      : selectedItemNature === "Upcoming" || selectedItemNature === "Past"
        ? categoryList.filter(
            (item) =>
              item.key !== "videos-and-presentations" &&
              item.key !== "articles-and-blogs",
          )
        : categoryList;
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectCategory = (category) => {
    setSelectedCategory(category);
    setPage(1);
    handleClose(); // Close the menu after selection
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handlePreviousClick = () => {
    if (page > 1) {
      setPage((page) => page - 1);
    }
  };

  const handleNextClick = () => {
    setPage((page) => page + 1);
  };

  const handleDeleteEvent = () => {
    setDeleting(true);
    baseFetch({
      url: `/v1/web/event/delete/${deleteEventId}`,
      method: "DELETE",
    })
      .then(() => {
        refresh();
        setOpen(false);
        dispatch(fetchProfileInfo());
        setDeleting(false);
      })
      .catch((err) => {
        console.log("err", err);
        setDeleting(false);
        if (err === "Please authenticate") {
          dispatch(toggleSessionModal(false));
        }
      });
  };

  const handleNavigation = (evt) => {
    if (
      selectedItemNature === "Past" ||
      selectedItemNature === "Pending" ||
      selectedItemNature === "Rejected"
    ) {
      navigate(
        `/events/edit-event/${evt?.id ?? evt?._id}?filter=${selectedItemNature}`,
      );
    } else {
      navigate(`/dashboard/details/${evt?.id ?? evt?._id}`, {
        state: { ...evt, filter: selectedItemNature },
      });
    }
  };

  const SHOW_POPUP =
    selectedItemNature === "Past" || selectedItemNature === "Posted";

  return (
    <>
      <Background dashboardChrome={true}>
        <Box
          sx={{
            paddingBottom: {
              xs: "50px",
              sm: 0,
            },
          }}
          className="dashboard-main-div"
        >
          <Box
            sx={{
              padding: {
                xs: "10px 0 20px",
                sm: "10px 0 20px",
                md: "10px 0 20px",
                lg: "10px 0 20px",
                xl: "20px 0 30px",
              },
            }}
          >
            <Container maxWidth="xl">
              <Grid container spacing={{ lg: 3, sm: 2, xs: 1 }}>
                <Grid
                  item
                  xs={12}
                  sm={11}
                  md={10}
                  lg={10}
                  xl={9}
                  sx={{
                    overflow: "auto",
                    margin: "auto",
                  }}
                >
                  <Box
                    sx={{
                      width: {
                        xs: "800px",
                        sm: "770px",
                        md: "770px",
                        lg: "auto",
                      },
                      overflowX: "auto",
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: {
                        xs: "5px",
                        sm: "10px",
                        md: "10px",
                        lg: 3,
                      }, // Optional spacing between elements
                      scrollbarWidth: "none", // For Firefox
                      msOverflowStyle: "none", // For Internet Explorer and Edge
                      "&::-webkit-scrollbar": {
                        display: "none", // For Chrome, Safari, and other WebKit browsers
                      },
                    }}
                  >
                    {/* Left section with status buttons */}
                    <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
                      {itemNature.map((item) => {
                        return (
                          <Button
                            key={item}
                            variant="contained"
                            onClick={() => setSelectedItemNature(item)}
                            sx={{
                              backgroundColor:
                                selectedItemNature === item
                                  ? "#2A4876"
                                  : "#7BB9D5",
                              boxShadow:
                                selectedItemNature === item
                                  ? "0 4px 4px 0 #2A487633"
                                  : "none",
                              color:
                                selectedItemNature === item
                                  ? "#fff"
                                  : "#152134",
                              borderRadius: "50px",
                              height: "50px",
                              textTransform: "none",
                              fontSize: "18px",
                              fontWeight: 400,
                              padding: "5px 30px",
                              marginBottom: {
                                xs: 0,
                                sm: "10px",
                              },
                            }}
                          >
                            {item}
                          </Button>
                        );
                      })}
                    </Box>

                    {/* Right section with filter button */}
                    {filterList?.length > 0 && (
                      <>
                        <Button
                          className="filter-button"
                          variant="outlined"
                          sx={{
                            borderRadius: "50px",
                            textTransform: "none",
                            padding: "6px 30px",
                            display: "flex",
                            alignItems: "center",
                            borderColor: "#AAB6C8",
                            height: "50px",
                            color: "#152134",
                            fontSize: "18px",
                            fontWeight: 400,
                          }}
                          onClick={handleClick}
                          startIcon={
                            <img
                              src="/images/icons/filter-icon.png"
                              alt=""
                              style={{ width: "24px", height: "24px" }}
                            />
                          }
                        >
                          Filter
                        </Button>
                        <Menu
                          anchorEl={anchorEl}
                          open={Boolean(anchorEl)}
                          onClose={handleClose}
                          sx={{
                            "& .MuiPaper-elevation": {
                              background: "#F1F7FA",
                              padding: "5px 10px",
                              borderRadius: "20px",
                              boxShadow: "0 5px 10px 0 #00000026",
                              marginTop: "10px",
                            },
                          }}
                        >
                          {filterList.map((item) => (
                            <MenuItem
                              sx={{
                                width: "100%",
                                background: "#FFFFFF",
                                padding: "10px",
                                borderRadius: "10px",
                                fontSize: "16px",
                                color: "#152134",
                                margin: "5px 0",
                              }}
                              key={item.key}
                              onClick={() => {
                                handleSelectCategory(item.key);
                              }}
                            >
                              {item.value}{" "}
                              {selectedCategory === item.key && (
                                <CheckIcon
                                  sx={{
                                    marginLeft: "auto",
                                    color: "#2A4876",
                                    width: "14px",
                                  }}
                                />
                              )}
                            </MenuItem>
                          ))}
                        </Menu>
                      </>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Container>
          </Box>
          <Box>
            <Container maxWidth="xl">
              <Grid container>
                <Grid
                  item
                  xs={12}
                  sm={11}
                  md={10}
                  lg={10}
                  xl={9}
                  sx={{ margin: "auto" }}
                >
                  {loading && <Loader />}
                  {!loading && (
                    <>
                      <Grid container spacing={{ lg: 3, sm: 2, xs: 2 }}>
                        {eventData?.length > 0 ? (
                          eventData?.map((evt) => {
                            return (
                              <Grid
                                item
                                xs={12}
                                sm={6}
                                md={6}
                                lg={6}
                                key={evt?.id ?? evt?._id}
                              >
                                <Box>
                                  <Box
                                    onClick={() => handleNavigation(evt)}
                                    sx={{
                                      width: "100%",
                                      height: "100%",
                                      overflow: "hidden",
                                      borderRadius: "20px",
                                      position: "relative",
                                      cursor: "pointer",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        position: "absolute",
                                        top: "10px",
                                        right: "10px",
                                        width: "auto",
                                        padding: "0 10px",
                                        height: "38px",
                                        borderRadius: "50px",
                                        background: "#fff",
                                        fontSize: {
                                          xs: "10px",
                                          sm: "10px",
                                          md: "12px",
                                          lg: "12px",
                                          xl: "14px",
                                        },
                                        fontWeight: 400,
                                        color: "#2A4876",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      {formatEventType("key", evt?.eventType)}
                                    </Box>
                                    <Box
                                      sx={{
                                        background:
                                          "linear-gradient(180deg, rgba(0, 0, 0, 0) 5.83%, #000000 100%)",
                                        width: "100%",
                                        height: "100%",
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                      }}
                                    ></Box>
                                    <Box
                                      component="img"
                                      src={evt?.eventPhoto}
                                      alt="logo"
                                      sx={{
                                        width: "100%",
                                        height: {
                                          xs: "200px",
                                          sm: "200px",
                                          md: "250px",
                                          lg: "300px",
                                          xl: "300px",
                                        },
                                        objectFit: "cover",
                                        borderRadius: "20px",
                                      }}
                                    />
                                    <Box
                                      sx={{
                                        width: "100%",
                                        position: "absolute",
                                        left: 0,
                                        bottom: 0,
                                        padding: {
                                          xs: "15px",
                                          sm: "15px",
                                          md: "15px",
                                          lg: "10px 10px 15px",
                                          xl: "20px",
                                        },
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "space-between",
                                          paddingBottom: "10px",
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            color: "#ffffff",
                                            fontSize: {
                                              xs: "16px",
                                              sm: "16px",
                                              md: "16px",
                                              lg: "16px",
                                              xl: "20px",
                                            },
                                            lineHeight: {
                                              xs: "20px",
                                              sm: "20px",
                                              md: "20px",
                                              lg: "20px",
                                              xl: "30px",
                                            },
                                            fontWeight: 600,
                                            textAlign: "left",
                                          }}
                                        >
                                          {evt?.eventTitle}
                                        </Typography>
                                      </Box>
                                      {showDateAndTime(evt) && (
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            paddingBottom: "10px",
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                            }}
                                          >
                                            <Box
                                              component="img"
                                              src="/images/icons/time-circle-icon.png"
                                              alt="logo"
                                              sx={{
                                                width: {
                                                  xs: "18px",
                                                  sm: "18px",
                                                  md: "18px",
                                                  lg: "12px",
                                                  xl: "18px",
                                                },
                                                height: {
                                                  xs: "18px",
                                                  sm: "18px",
                                                  md: "18px",
                                                  lg: "12px",
                                                  xl: "18px",
                                                },
                                              }}
                                            />
                                            <Typography
                                              sx={{
                                                color: "#ffffff",
                                                fontSize: {
                                                  xs: "12px",
                                                  sm: "12px",
                                                  md: "12px",
                                                  lg: "12px",
                                                  xl: "16px",
                                                },
                                                fontWeight: 400,
                                                paddingLeft: {
                                                  xs: "10px",
                                                  sm: "10px",
                                                  md: "10px",
                                                  lg: "5px",
                                                  xl: "10px",
                                                },
                                                textAlign: "left",
                                              }}
                                            >
                                              {evt?.eventTimeStart}-
                                              {evt?.eventTimeEnd}
                                            </Typography>
                                          </Box>
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              paddingLeft: {
                                                xs: "10px",
                                                sm: "10px",
                                                md: "10px",
                                                lg: "15px",
                                                xl: "20px",
                                              },
                                            }}
                                          >
                                            <Box
                                              component="img"
                                              src="/images/icons/calendar-icon-white.png"
                                              alt="logo"
                                              sx={{
                                                width: {
                                                  xs: "18px",
                                                  sm: "18px",
                                                  md: "18px",
                                                  lg: "12px",
                                                  xl: "18px",
                                                },
                                                height: {
                                                  xs: "18px",
                                                  sm: "18px",
                                                  md: "18px",
                                                  lg: "12px",
                                                  xl: "18px",
                                                },
                                              }}
                                            />
                                            <Typography
                                              sx={{
                                                color: "#ffffff",
                                                fontSize: {
                                                  xs: "12px",
                                                  sm: "12px",
                                                  md: "12px",
                                                  lg: "12px",
                                                  xl: "16px",
                                                },
                                                fontWeight: 400,
                                                paddingLeft: {
                                                  xs: "10px",
                                                  sm: "10px",
                                                  md: "10px",
                                                  lg: "5px",
                                                  xl: "10px",
                                                },
                                                textAlign: "left",
                                              }}
                                            >
                                              {moment
                                                .utc(evt?.eventDateStart)
                                                .format("MMM DD, YYYY")}
                                            </Typography>
                                          </Box>
                                        </Box>
                                      )}
                                    </Box>
                                  </Box>
                                  {selectedItemNature !== "Upcoming" && (
                                    <Box>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "space-between",
                                          padding: "10px 0 10px",
                                          gap: "20px",
                                        }}
                                      >
                                        {selectedItemNature != "Past" && (
                                          <Button
                                            onClick={() => {
                                              setOpen(true);
                                              setDeleteEventId(
                                                evt?.id ?? evt?._id,
                                              );
                                            }}
                                            variant="contained"
                                            sx={{
                                              backgroundColor: "#fff",
                                              color: "#2A4876",
                                              textTransform: "none",
                                              padding: "10px 20px",
                                              borderRadius: "20px",
                                              width: "50%",
                                            }}
                                          >
                                            Delete
                                          </Button>
                                        )}
                                        <Button
                                          onClick={() => {
                                            if (!SHOW_POPUP) {
                                              navigate(
                                                `/events/edit-event/${evt?.id ?? evt?._id}?filter=${selectedItemNature}`,
                                              );
                                            } else if (
                                              contentSizeInfo?.notAllowedContent
                                                ?.length >= 0 &&
                                              contentSizeInfo?.allowedContent
                                                ?.length !== 0
                                            ) {
                                              navigate(
                                                `/events/edit-event/${evt?.id ?? evt?._id}?filter=${selectedItemNature}`,
                                              );
                                            } else {
                                              setOpenPostLimitModal(true);
                                            }
                                          }}
                                          variant="contained"
                                          sx={{
                                            backgroundColor:
                                              selectedItemNature === "Posted" ||
                                              selectedItemNature === "Past"
                                                ? "#7BB9D5"
                                                : "#2A4876",
                                            color:
                                              selectedItemNature === "Posted" ||
                                              selectedItemNature === "Past"
                                                ? "#152134"
                                                : "#fff",
                                            textTransform: "none",
                                            padding: "10px 20px",
                                            borderRadius: "20px",
                                            width:
                                              selectedItemNature === "Past"
                                                ? "100%"
                                                : "50%",
                                          }}
                                        >
                                          {selectedItemNature === "Posted" ||
                                          selectedItemNature === "Past"
                                            ? "Duplicate"
                                            : // : selectedItemNature === 'Past'
                                              //   ? 'Retrieve'
                                              "Edit"}
                                        </Button>
                                      </Box>
                                    </Box>
                                  )}
                                  {(selectedItemNature === "Upcoming" ||
                                    selectedItemNature === "Posted") && (
                                    <Button
                                      onClick={() =>
                                        navigate(
                                          `/events/edit-event/${evt?.id ?? evt?._id}?filter=${selectedItemNature}&isEdit=${true}`,
                                        )
                                      }
                                      variant="contained"
                                      sx={{
                                        backgroundColor: "#2A4876",
                                        color: "#fff",
                                        textTransform: "none",
                                        padding: "10px 20px",
                                        borderRadius: "20px",
                                        width: "100%",
                                        mt:
                                          selectedItemNature === "Posted"
                                            ? 0
                                            : 1,
                                      }}
                                    >
                                      Edit
                                    </Button>
                                  )}
                                </Box>
                              </Grid>
                            );
                          })
                        ) : selectedItemNature != "Rejected" ? (
                          <Grid item xs={12}>
                            <Box
                              sx={{
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexDirection: "column",
                                textAlign: "center",
                                padding: { xs: "30px", sm: "40px", md: "60px" },
                              }}
                            >
                              <Box
                                sx={{
                                  background: "#fff",
                                  borderRadius: "20px",
                                  padding: { xs: "25px 20px", sm: "40px 30px" },
                                  maxWidth: "400px",
                                  width: "100%",
                                  boxShadow: "0px 4px 20px rgba(0,0,0,0.1)",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: {
                                      xs: "20px",
                                      sm: "24px",
                                      md: "28px",
                                    },
                                    fontWeight: 700,
                                    color: "#2A4876",
                                    marginBottom: "15px",
                                  }}
                                >
                                  No Content Here Yet
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: { xs: "14px", sm: "16px" },
                                    color: "#2A4876",
                                    fontWeight: 400,
                                    marginBottom: "25px",
                                    lineHeight: 1.6,
                                    textAlign: "left",
                                  }}
                                >
                                  This is your space to showcase your
                                  company&apos;s insights, values, vision, and
                                  achievements to aviation professionals at
                                  every stage of their careers! Start curating
                                  content now to connect with Echo&apos;s
                                  vibrant community, highlight your expertise,
                                  and spotlight your brand.
                                </Typography>

                                <Button
                                  variant="contained"
                                  onClick={() => {
                                    if (
                                      contentSizeInfo?.notAllowedContent
                                        ?.length >= 0 &&
                                      contentSizeInfo?.allowedContent
                                        ?.length !== 0
                                    ) {
                                      navigate("/events/create-event");
                                    } else {
                                      setOpenPostLimitModal(true);
                                    }
                                  }}
                                  sx={{
                                    background: "#2A4876",
                                    color: "#fff",
                                    borderRadius: "25px",
                                    padding: {
                                      xs: "8px 18px",
                                      sm: "10px 24px",
                                    },
                                    fontSize: { xs: "14px", sm: "16px" },
                                    fontWeight: 600,
                                    textTransform: "none",
                                    "&:hover": {
                                      background: "#1f3454",
                                    },
                                  }}
                                >
                                  Post Now
                                </Button>
                              </Box>
                            </Box>
                          </Grid>
                        ) : (
                          ""
                        )}
                      </Grid>
                      {totalRecords > 12 && (
                        <Box
                          sx={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            padding: {
                              xs: "15px 0",
                              sm: "30px",
                              md: "30px",
                              lg: "40px",
                            },
                            "& ul li:last-child": {
                              position: {
                                xs: "unset",
                                md: "absolute",
                              },
                              right: "10%",
                            },
                            "& ul li:first-child": {
                              position: {
                                xs: "unset",
                                md: "absolute",
                              },
                              left: "10%",
                            },
                          }}
                        >
                          <Pagination
                            count={Math.ceil(totalRecords / 12)}
                            page={page}
                            onChange={handlePageChange}
                            renderItem={(item) => (
                              <PaginationItem
                                {...item}
                                slots={paginationSlots}
                                slotProps={{
                                  previous: { onClick: handlePreviousClick },
                                  next: {
                                    onClick: handleNextClick,
                                    disabled:
                                      page === Math.ceil(totalRecords / 12),
                                  },
                                }}
                              />
                            )}
                          />
                        </Box>
                      )}
                    </>
                  )}
                </Grid>
              </Grid>
            </Container>
            <Button
              sx={{
                zIndex: "9999",
                position: "fixed",
                right: "30px",
                bottom: "70px",
                backgroundColor: "#2A4876",
                width: {
                  xs: "50px",
                  sm: "50px",
                  md: "55px",
                  lg: "55px",
                  xl: "60px",
                },
                height: {
                  xs: "50px",
                  sm: "50px",
                  md: "55px",
                  lg: "55px",
                  xl: "60px",
                },
                borderRadius: "50%",
                padding: 0,
                minWidth: "auto",
              }}
              onClick={() => {
                if (
                  contentSizeInfo?.notAllowedContent?.length >= 0 &&
                  contentSizeInfo?.allowedContent?.length !== 0
                ) {
                  navigate("/events/create-event");
                } else {
                  setOpenPostLimitModal(true);
                }
              }}
              color="primary"
            >
              <img src={PlusIcon} />
            </Button>
          </Box>
        </Box>
        {open && (
          <DeleteEventPopup
            handleConfirm={() => handleDeleteEvent()}
            handleClose={() => setOpen(false)}
            open={open}
          />
        )}
        {openPostLimitModal && (
          <PostingLimitModal
            open={openPostLimitModal}
            onClose={() => setOpenPostLimitModal(false)}
            maxedOutContent={contentSizeInfo?.notAllowedContent}
            allowedContent={contentSizeInfo?.allowedContent}
          />
        )}
      </Background>
    </>
  );
}
